import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../core/environments/environment';
import {
  VerifyCodeRequestBody,
  VerifyCodeResponse,
  EndPoint,
} from '../../../core/models/auth.model';
import { CookieService } from 'ngx-cookie-service';
import { UsersService } from '../../users/services/users.service';
import { AuthService } from './auth.service';

import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class VerifyCodeService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;
  private readonly _cookieService = inject(CookieService);
  private readonly _usersService = inject(UsersService);
  private readonly _authService = inject(AuthService);
  private readonly ENCRYPTION_PASSWORD = 'declarabid-a2be4654943758e5d205';

  public isFirstlog = signal<boolean>(false);
  public pendingAuthToken = signal<string>('');

  private encrypt(text: string): string {
    return CryptoJS.AES.encrypt(text, this.ENCRYPTION_PASSWORD).toString();
  }

  public verifyCode({
    user_id,
    code,
  }: VerifyCodeRequestBody): Observable<VerifyCodeResponse> {
    const payload = new HttpParams()
      .set('user_id', user_id.toString())
      .set('code', code);

    return this._http
      .post<VerifyCodeResponse>(
        `${this._apiUrl}${EndPoint.validateLogin}`,
        payload,
      )
      .pipe(
        tap({
          next: ({ token }) => {
            if (token) {
              this.isFirstlog.set(token.change_password);

              if (!token.change_password) {
                this.pendingAuthToken.set(token.token);
              } else {
                this.storeTokenOnFirstLogin(token.token);
              }
            }
          },
          error: (error) => {
            console.error(
              'Error en verificación:',
              error.error?.message || error.message,
            );
          },
        }),
      );
  }

  public firstTimeChangePassword(
    newPassword: string,
    tokenFirstPassword: string,
  ): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${tokenFirstPassword}`,
      'Content-Type': 'application/json',
    });

    const encryptedPassword = this.encrypt(newPassword);
    const payload = { newPassword: encryptedPassword };

    return this._http
      .post<any>(
        `${this._apiUrl}${EndPoint.postFirstChangePassword}`,
        payload,
        { headers },
      )
      .pipe(
        tap({
          next: (response) => {
            if (
              response.code === '200' &&
              response.message === 'Updated password'
            ) {
              this.isFirstlog.set(true);

              const storedToken = this.pendingAuthToken();
              if (storedToken) {
                this.storeTokenOnFirstLogin(storedToken);
                this.pendingAuthToken.set('');
              } else {
                console.warn('⚠️ No se encontró token pendiente');
              }
            }
          },
          error: (error) => {
            console.error(
              'Error en cambio de contraseña:',
              error.error?.message || error.message,
            );
          },
        }),
        catchError((error) => {
          console.error('Error crítico:', error);
          throw error;
        }),
      );
  }

  public storeTokenOnFirstLogin(token: string): void {
    if (this.isFirstlog()) {
      this._authService.handleSuccessfulLogin(token);
    }
  }
}
